import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Sparkles, Zap, Newspaper } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useBrands } from "@/lib/BrandsProvider"

interface CampaignTypeSelectionProps {
  isOpen: boolean
  onClose: () => void
  isPrimaryBrandCampaign?: boolean
}

export type CampaignType = 'BASE' | 'CUSTOM' | 'CONVERGENCE_BASED';
export function CampaignTypeSelection({ isOpen, onClose, isPrimaryBrandCampaign = false }: CampaignTypeSelectionProps) {
  const navigate = useNavigate()
  const { selectedBrand } = useBrands()

  const handleTypeSelection = (type: string) => {
    if (!selectedBrand) return

    // Navigate to the new campaign form with the selected type and primary flag
    navigate(`/campaigns/new`, {
      state: { 
        campaignType: type,
        brandId: selectedBrand.id,
        isPrimaryBrandCampaign
      }
    })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] p-8 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="mb-6">
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogDescription>
            Choose the type of campaign you want to create.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <Button
            variant="outline"
            className="w-full flex flex-col items-center justify-center p-8 h-auto gap-4 hover:bg-muted/50"
            onClick={() => handleTypeSelection('BASE')}
          >
            <div className="bg-muted rounded-full p-6">
              <Newspaper className="h-8 w-8" />
            </div>
            <div className="space-y-3 text-center w-full max-w-lg">
              <h3 className="font-semibold text-lg">Brand Campaign</h3>
              <p className="text-sm text-muted-foreground whitespace-normal">
                A campaign based on your brand. Typically used for broad market campaigns and collateral like Websites.
              </p>
            </div>
          </Button>

          <Button
            variant="outline"
            className="w-full flex flex-col items-center justify-center p-8 h-auto gap-4 hover:bg-muted/50"
            onClick={() => handleTypeSelection('CUSTOM')}
          >
            <div className="bg-muted rounded-full p-6">
              <Zap className="h-8 w-8" />
            </div>
            <div className="space-y-3 text-center w-full max-w-lg">
              <h3 className="font-semibold text-lg">Custom Campaign</h3>
              <p className="text-sm text-muted-foreground whitespace-normal">
                Start with your own idea or strategy. Craft a custom campaign tailored to your specific needs.
              </p>
            </div>
          </Button>

          <Button
            variant="outline"
            className="w-full flex flex-col items-center justify-center p-8 h-auto gap-4 hover:bg-muted/50"
            onClick={() => handleTypeSelection('CONVERGENCE_BASED')}
          >
            <div className="bg-muted rounded-full p-6">
              <Sparkles className="h-8 w-8" />
            </div>
            <div className="space-y-3 text-center w-full max-w-lg">
              <h3 className="font-semibold text-lg">From Convergence</h3>
              <p className="text-sm text-muted-foreground whitespace-normal">
                Transform an AI-discovered convergence opportunity into a targeted marketing campaign.
              </p>
            </div>
          </Button>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
